import { FC, useEffect, useState } from "react";
import { Tender, Tendering } from "../../../models/tendering";
import { Button, Card, CardHeader, CardPreview, Divider, Link, Spinner, Text, Title3 } from "@fluentui/react-components";
import { createNote, deleteNote, getNotes, updateNote } from "../../../services/noteService";
import { Note, NoteCreateDTO } from "../../../models/note";
import NewNote from "../Dialogs/NewNote";
import { DeleteRegular, EditRegular, NotepadRegular } from "@fluentui/react-icons";
import { useAppContext } from "../../../AppContext";
import { RoleDTO } from "../../../models/role";

interface TenderLogsProps{
    tenderDocument: Tender;
    tenderingDocument: Tendering;
}

const TenderLogs: FC<TenderLogsProps> = (props: TenderLogsProps) => {
    const [notes, setNotes] = useState<Note[]>([]);
    const [editNote, setEditNote] = useState<Note | undefined>(undefined);
    const [notesLoading, setNotesLoading] = useState<boolean>(true);
    const [newNoteOpen, setNewNoteOpen] = useState<boolean>(false);
    const [deleteLoading, setDeleteLoading] = useState<number | null>(null);
    // Delete note handler
    const handleDeleteNote = async (noteID: number) => {
      setDeleteLoading(noteID);
      try {
        await deleteNote(noteID);
        setNotes(prev => prev.filter(n => n.noteID !== noteID));
      } catch (error) {
        console.error("Error deleting note:", error);
      }
      setDeleteLoading(null);
    };

    const {user, featurePermissions: loggedUsersFPs,  getFeaturePermissionsByModuleID} = useAppContext();
    const featurePermissions = user.roles.some((r: RoleDTO) => r.roleType == "Supplier") ? getFeaturePermissionsByModuleID(props.tenderDocument.tenderID) : loggedUsersFPs;


    useEffect(() => {
      const fetchNotes = async () => {
        const resultNotes = await getNotes(props.tenderDocument.tenderID);
        setNotes(resultNotes);
        if(!featurePermissions?.some(fp => fp.action == 'Tender page/Notes/See non-public notes')){
          setNotes(resultNotes.filter((n: Note) => n.isNotePublic));
        }
        setNotesLoading(false);
      };

      if(featurePermissions?.some(fp => fp.action == 'Tender page/Notes')){
        fetchNotes();
      }
    }, []);

    const handleSaveNote = async (noteData: NoteCreateDTO) => {
      try{
        if(editNote){
          await updateNote(editNote.noteID, noteData).then((res:Note) => {
            setNotes(prev => prev.map(n => n.noteID === res.noteID ? {
              ...n,
              noteHeader: noteData.noteHeader,
              noteSubject: noteData.noteSubject,
              noteBody: noteData.noteBody,
              isNotePublic: noteData.isNotePublic,
              noteDate: noteData.noteDate
            } : n));
            setEditNote(undefined);
            setNewNoteOpen(false);
          });
        }
        else{
          await createNote(props.tenderDocument.tenderID, noteData).then((res) => {
            setNotes(prev => [res, ...prev]);
            setNewNoteOpen(false);
          });
        }
      }
      catch (error) {
        console.error("Error creating note:", error);
      }
    };

    return (
    <>
      <div className="flex flex-col gap-4 items-start">
        {featurePermissions?.some(fp => fp.action == 'Tender page/Notes/Create note') && <Button disabled={notesLoading} appearance="primary" onClick={() => setNewNoteOpen(true)}>Lägg till ny loggbok</Button>}
        {!notesLoading && notes.map((note) => (
            <Card className="w-[100%]" key={note.noteID}>
            <CardPreview>
              <div>
                <div className="flex justify-between items-center">
                  <Title3>{note.noteHeader}</Title3>
                  <div className="flex items-center gap-2">
                    <Text size={200} weight="regular">{note.noteSubject}</Text>
                    <Divider vertical />
                    <Text size={200} weight="regular">{note.isNotePublic ? "Publikt" : "Ej publikt"}</Text>
                    <Divider vertical />
                    <Text size={200} weight="regular">{new Date(note.noteDate).toLocaleDateString('sv-SE')}</Text>
                    {user.roles.some((r: RoleDTO) => r.roleType === "General") && <>
                      <Divider vertical />
                      <Text size={200} weight="regular">Av: {note.noteCreatedByUser.firstName + " " + note.noteCreatedByUser.lastName}</Text>
                    </>}
                    {featurePermissions?.some(fp => fp.action == 'Tender page/Notes/Edit note') && (
                      <><Divider vertical /><Button
                        size="small"
                        appearance="subtle"
                        onClick={() => {
                          setEditNote(note);
                          setNewNoteOpen(true);
                        } }
                        icon={<EditRegular />}>
                      </Button></>
                    )}
                    {featurePermissions?.some(fp => fp.action == 'Tender page/Notes/Delete note') && (
                      <>
                        <Divider vertical />
                        <Button 
                          size="small" 
                          appearance="subtle" 
                          onClick={() => handleDeleteNote(note.noteID)}
                          icon={<DeleteRegular />} 
                          disabled={deleteLoading === note.noteID}
                        >
                          {deleteLoading === note.noteID ? <Spinner size="tiny" /> : null}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </CardPreview>
            <div className="rich-text-content" dangerouslySetInnerHTML={{ __html: note.noteBody }}></div>
          </Card>
        ))}
        {!notesLoading && notes.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-10 w-full text-gray-400">
            <NotepadRegular fontSize={40} />
            <Text size={300} italic>Inga loggböcker hittades.</Text>
          </div>
        )}
        {notesLoading && <Spinner label={"Laddar loggbok..."} />}
        {deleteLoading && <Spinner label={"Tar bort loggbok..."} />}
      </div>
      <NewNote
        note={editNote}
        open={newNoteOpen}
        onClose={() => setNewNoteOpen(false)}
        onSave={handleSaveNote}
      />
    </>
    );
}

export default TenderLogs;